import React from 'react';
import Header from 'shared/components/header/Header';
import {
  GetChannelLookupAPI,
  GetBreakChannelTemplateAPI,
  ImportBreakPatternsAPI,
  DeleteBreakChannelTemplateAPI,
} from 'api';
import './BreakPatterns.css';
import EditIcon from 'assets/icons/EditIcon.svg';
import { trashCanSmIcon } from 'assets';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';
import moment from 'moment';
import useNotification from 'store/NotificationStore';
import BreakPatternEditor from './BreakPatternEditor';
import DeleteModalPrompt from './modals/DeleteModalPrompt';
import { OpDataGrid } from 'op2mise-react-widgets';
import { filterParams } from 'op2mise-react-widgets/OpDataGrid/utils';
import useStore from 'store/AccountStore';
import { getChannelSession } from 'utils';
import OpSelect from 'op2mise-react-widgets/OpSelect/OpSelect';
import { AddButton } from 'shared/components';

const BreakPatterns = () => {
  const { success } = useNotification((state) => state);
  const { user, updateChannel } = useStore((state) => state);
  const [channelsList, setChannelsList] = React.useState([]);
  const [selectedChannel, setSelectedChannel] = React.useState(0);

  const [breakPatterns, setBreakPatterns] = React.useState([]);
  const [selectedRow, setSelectedRow] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [mode, setMode] = React.useState('view'); // view, edit, new
  const [versionName, setVersionName] = React.useState('');
  const [channelInfo, setChannelInfo] = React.useState({
    channelId: 0,
    channelName: null,
  });
  const [confirmModal, setConfirmModal] = React.useState(false);

  const getSession = (res) => {
    const { channelId } = user;
    var sessionChannel = getChannelSession(channelId, res);
    handleSelectChannel(sessionChannel);
  };

  const getChannelsAPI = () => {
    GetChannelLookupAPI({
      onSuccess: (res) => {
        if (res.length) {
          setChannelsList(res);
          getSession(res);
        }
      },
      setLoader: setIsLoading,
    });
  };

  const handleDelete = (args) => {
    const { id } = args;
    DeleteBreakChannelTemplateAPI({
      queryParams: {
        id: id,
      },
      onSuccess: (res) => {
        success('Successfully deleted!');
        fetchBreakPatterns(channelInfo.channelId);
        setConfirmModal(false);
      },
    });
  };

  // Format created date to DD/MM/YYYY HH:mm
  const formattedDate = (params) =>
    params.data.createdDate !== null
      ? moment(params.data.createdDate).format('DD/MM/YYYY HH:mm')
      : '';

  const actionColumnCellRenderer = (params) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
        height: '100%',
        width: '100%',
      }}
    >
      <img
        src={EditIcon}
        alt="Edit Pattern"
        title="Edit Pattern"
        onClick={() => {
          setMode('edit');
          setSelectedRow({
            ...params.data,
          });
          setVersionName(params.data.version);
        }}
        style={{
          marginTop: '5px',
          cursor: 'pointer',
        }}
      />
      <img
        src={trashCanSmIcon}
        alt="Delete Pattern"
        title="Delete Pattern"
        onClick={() => {
          setConfirmModal(true);
          setSelectedRow(params.data);
        }}
        style={{
          marginTop: '5px',
          cursor: 'pointer',
        }}
      />
    </div>
  );
  // Column definitions for the data grid
  const columnDefinitions = React.useMemo(
    () => [
      {
        field: 'version',
        headerName: 'Pattern Name',
        sort: 'asc',
        flex: 1,
      },
      {
        field: 'createdDate',
        headerName: 'Date Created',
        flex: 1,
        valueFormatter: formattedDate,
        filter: 'agDateColumnFilter',
        filterParams: filterParams,
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
      },
      {
        field: 'default',
        headerName: 'Default',
        cellDataType: 'text',
        flex: 1,
        filterParams: {
          valueFormatter: (params) => {
            return params.value ? 'Yes' : 'No';
          },
        },
        valueFormatter: (params) => {
          return params.value ? 'Yes' : 'No';
        },
      },
      {
        flex: 1,
        field: 'action',
        minWidth: 100,
        cellDataType: 'text',
        cellRenderer: actionColumnCellRenderer,
      },
    ],
    []
  );

  const handleSelectChannel = (args) => {
    setSelectedChannel(args.channelId), setChannelInfo(args);
    //save session
    updateChannel(args);
  };

  const getHighestId = (obj) => {
    let temp = 0;
    if (obj.length > 0) {
      obj.map((item) => {
        if (item.id > temp) {
          temp = item.id;
        }
      });
    }
    return temp;
  };

  const fetchBreakPatterns = (channelId) => {
    GetBreakChannelTemplateAPI({
      queryParams: { channelId },
      onSuccess: (res) => {
        setBreakPatterns(res);
      },
      setLoader: setIsLoading,
    });
  };

  const handleImportBreakTypes = async (file) => {
    const { channelId } = channelInfo;
    const uploadResults = await ImportBreakPatternsAPI({
      channelId,
      file,
    });
    return uploadResults;
  };

  const props = React.useMemo(
    () => ({
      mode: mode,
      setMode: (params) => setMode(params),
      onClose: () => {
        setMode('view');
      },
      onImport: handleImportBreakTypes,
      selectedRow: selectedRow,
      channelInfo: channelInfo,
    }),
    [mode, selectedRow, selectedChannel]
  );

  // Returns all fields from columnDefinition except the action column
  const headerFields = columnDefinitions
    .filter((column) => column.field !== 'action')
    .map((column) => column.field);

  React.useEffect(() => {
    getChannelsAPI();
  }, []);

  React.useEffect(() => {
    const { channelId } = channelInfo;
    if (mode === 'view') {
      fetchBreakPatterns(channelId);
      setSelectedRow({});
    }
  }, [channelInfo, mode]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title={
          <div>
            <span>Break Patterns</span>
          </div>
        }
        childComponent={
          <>
            <div
              className="d-flex align-items-center"
              style={{ marginBottom: '5px', marginRight: '7px' }}
            >
              {/* Channels Selection/Dropdown */}
              {/* {mode === 'view' || mode === 'edit' ? ( */}
              <div className="break-banner-selector">
                <div>
                  {/** Re-locate this re-usable component to `op2mise-react-widgets */}
                  <OpSelect
                    allowFiltering={true}
                    dataSource={channelsList?.map((item) => ({
                      id: item.channelId,
                      name: item.channelName,
                      ...item,
                    }))}
                    fields={{ text: 'name', value: 'id' }}
                    filterBarPlaceholder="Search"
                    onChange={(e) => {
                      handleSelectChannel(e.target.itemData);
                    }}
                    placeholder={'Please select a channel'}
                    value={selectedChannel}
                    width={150}
                    enabled={mode !== 'edit'}
                  />
                </div>
              </div>
            </div>
            <AddButton
              onClick={() => {
                setMode('new');
                setSelectedRow({
                  id: null,
                  version: '',
                  status: 'Inactive',
                  default: false,
                  contentLevel: {
                    eventType: null,
                    eventName: '',
                    eventDurationFrom: null,
                    eventDurationTo: null,
                    timeOfDayFrom: null,
                    timeOfDayTo: null,
                    periodFrom: null,
                    periodTo: null,
                  },
                });
              }}
              hidden={mode !== 'view'}
              disabled={channelsList.length === 0 || selectedChannel === 0}
            />
          </>
        }
      />
      <div style={{ display: 'flex', flex: 1, width: '100%'  }}>
        {isLoading ? (
          <div style={{ display: 'flex', flex: 1, width: '100%', alignContent: 'center' }}>
            <BootstrapSpinner pushTop="-9px" />
          </div>
        ) : channelsList.length ? (
          mode === 'view' ? (
            <OpDataGrid
              columns={columnDefinitions}
              customVerbiage="No patterns found"
              enableMultipleRowSelection={false}
              gridHeightBuffer={177}
              gridName="channelSetup-breakPatterns"
              resizable
              rows={breakPatterns}
              filter
              sortable
              sharedColumnProperties={{
                cellStyle: {
                  fields: headerFields,
                  value: { textAlign: 'left' },
                },
              }}
              showAlternativeRowHighlight
            />
          ) : (
            <BreakPatternEditor key={selectedRow?.id ?? 'new'} {...props} />
          )
        ) : null}
      </div>
      <DeleteModalPrompt
        show={confirmModal}
        onHide={() => {
          setConfirmModal(false);
        }}
        onProceed={() => {
          handleDelete(selectedRow);
        }}
        message={null}
      />
    </div>
  );
};

export default BreakPatterns;
